import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import PeopleIcon from '@material-ui/icons/People';
import BuildIcon from '@material-ui/icons/Build';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import StorageIcon from '@material-ui/icons/Storage';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { KPICardsSkeleton } from './SkeletonComponents';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  iconContainer: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: 32,
    color: '#fff',
  },
  value: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  trend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  trendUp: {
    color: '#4caf50',
  },
  trendDown: {
    color: '#f44336',
  },
  trendStable: {
    color: '#757575',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  subLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  allTimeLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontStyle: 'italic',
  },
  // Color variants
  blue: {
    backgroundColor: '#2196f3',
  },
  green: {
    backgroundColor: '#4caf50',
  },
  orange: {
    backgroundColor: '#ff9800',
  },
  purple: {
    backgroundColor: '#9c27b0',
  },
  red: {
    backgroundColor: '#f44336',
  },
  teal: {
    backgroundColor: '#009688',
  },
  indigo: {
    backgroundColor: '#3f51b5',
  },
  pink: {
    backgroundColor: '#e91e63',
  },
}));

// Card with trend indicator (for monthly comparisons)
const TrendingKPICard = ({ title, value, change, changePercent, trend, icon: Icon, colorClass }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon className={classes.trendUp} fontSize="small" />;
      case 'down':
        return <TrendingDownIcon className={classes.trendDown} fontSize="small" />;
      default:
        return <TrendingFlatIcon className={classes.trendStable} fontSize="small" />;
    }
  };

  const getTrendClass = () => {
    switch (trend) {
      case 'up':
        return classes.trendUp;
      case 'down':
        return classes.trendDown;
      default:
        return classes.trendStable;
    }
  };

  return (
    <Paper className={classes.paper} elevation={2}>
      <div className={classes.header}>
        <Box>
          <Typography variant="body2" className={classes.label}>
            {title}
          </Typography>
          <Typography variant="h4" className={classes.value}>
            {value}
          </Typography>
        </Box>
        <Box className={`${classes.iconContainer} ${colorClass}`}>
          <Icon className={classes.icon} />
        </Box>
      </div>
      <Box className={classes.trend}>
        {getTrendIcon()}
        <Typography variant="body2" className={getTrendClass()}>
          {change >= 0 ? '+' : ''}{change} ({changePercent >= 0 ? '+' : ''}{changePercent}%)
        </Typography>
        <Typography variant="caption" className={classes.label}>
          {t('dashboard.vs_last_month')}
        </Typography>
      </Box>
    </Paper>
  );
};

// Simple card for all-time stats (no trend)
const SimpleKPICard = ({ title, value, subtext, icon: Icon, colorClass }) => {
  const classes = useStyles();

  return (
    <Paper className={classes.paper} elevation={2}>
      <div className={classes.header}>
        <Box>
          <Typography variant="body2" className={classes.label}>
            {title}
          </Typography>
          <Typography variant="h4" className={classes.value}>
            {value}
          </Typography>
        </Box>
        <Box className={`${classes.iconContainer} ${colorClass}`}>
          <Icon className={classes.icon} />
        </Box>
      </div>
      <Typography variant="caption" className={classes.allTimeLabel}>
        {subtext}
      </Typography>
    </Paper>
  );
};

// Completion rate card (special design)
const CompletionRateCard = ({ value, pendingCount }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Paper className={classes.paper} elevation={2}>
      <div className={classes.header}>
        <Box>
          <Typography variant="body2" className={classes.label}>
            {t('dashboard.kpi.completion_rate')}
          </Typography>
          <Typography variant="h4" className={classes.value}>
            {Math.round(value || 0)}%
          </Typography>
        </Box>
        <Box className={`${classes.iconContainer} ${classes.red}`}>
          <CheckCircleIcon className={classes.icon} />
        </Box>
      </div>
      <Typography variant="caption" className={classes.subLabel}>
        {pendingCount || 0} {t('dashboard.pending')}
      </Typography>
    </Paper>
  );
};

const KPICards = ({ metrics }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!metrics) return <KPICardsSkeleton />;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Grid container spacing={3} className={classes.root}>
      {/* ROW 1 */}
      
      {/* New Customers Card */}
      <Grid item xs={12} sm={6} md={3}>
        <TrendingKPICard
          title={t('dashboard.kpi.new_customers')}
          value={metrics.newCustomers?.current || 0}
          change={metrics.newCustomers?.change || 0}
          changePercent={metrics.newCustomers?.changePercent || 0}
          trend={metrics.newCustomers?.trend || 'stable'}
          icon={PeopleIcon}
          colorClass={classes.blue}
        />
      </Grid>

      {/* Incidences Card */}
      <Grid item xs={12} sm={6} md={3}>
        <TrendingKPICard
          title={t('dashboard.kpi.incidences')}
          value={metrics.incidences?.current || 0}
          change={metrics.incidences?.change || 0}
          changePercent={metrics.incidences?.changePercent || 0}
          trend={metrics.incidences?.trend || 'stable'}
          icon={BuildIcon}
          colorClass={classes.orange}
        />
      </Grid>

      {/* Assistance Card */}
      <Grid item xs={12} sm={6} md={3}>
        <TrendingKPICard
          title={t('dashboard.kpi.assistance')}
          value={metrics.assistance?.current || 0}
          change={metrics.assistance?.change || 0}
          changePercent={metrics.assistance?.changePercent || 0}
          trend={metrics.assistance?.trend || 'stable'}
          icon={AssignmentIcon}
          colorClass={classes.teal}
        />
      </Grid>

      {/* Orders Card */}
      <Grid item xs={12} sm={6} md={3}>
        <TrendingKPICard
          title={t('dashboard.kpi.orders')}
          value={metrics.orders?.current || 0}
          change={metrics.orders?.change || 0}
          changePercent={metrics.orders?.changePercent || 0}
          trend={metrics.orders?.trend || 'stable'}
          icon={ShoppingCartIcon}
          colorClass={classes.purple}
        />
      </Grid>

      {/* ROW 2 */}

      {/* Revenue Card */}
      <Grid item xs={12} sm={6} md={3}>
        <TrendingKPICard
          title={t('dashboard.kpi.revenue')}
          value={formatCurrency(metrics.revenue?.current || 0)}
          change={metrics.revenue?.change || 0}
          changePercent={metrics.revenue?.changePercent || 0}
          trend={metrics.revenue?.trend || 'stable'}
          icon={AttachMoneyIcon}
          colorClass={classes.green}
        />
      </Grid>

      {/* Completion Rate Card */}
      <Grid item xs={12} sm={6} md={3}>
        <CompletionRateCard
          value={metrics.completionRate}
          pendingCount={metrics.pendingIncidences}
        />
      </Grid>

      {/* Total Customers All Time */}
      <Grid item xs={12} sm={6} md={3}>
        <SimpleKPICard
          title={t('dashboard.kpi.total_customers')}
          value={metrics.totalCustomersAllTime || 0}
          subtext={t('dashboard.all_time')}
          icon={StorageIcon}
          colorClass={classes.indigo}
        />
      </Grid>

      {/* Total Incidences All Time */}
      <Grid item xs={12} sm={6} md={3}>
        <SimpleKPICard
          title={t('dashboard.kpi.total_incidences')}
          value={metrics.totalIncidencesAllTime || 0}
          subtext={t('dashboard.all_time')}
          icon={BuildIcon}
          colorClass={classes.pink}
        />
      </Grid>
    </Grid>
  );
};

export default KPICards;
